import React, { useMemo } from 'react';
import { Switch, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, ThemeProvider, useTheme } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import NavigationBar from '../../components/core/NavigationBar';
import DialogRoot, { registerDialog } from '../../components/dialog/DialogRoot';
import ProcessingShorelinePage from '../Shoreline/result.jsx';
import ProcessingBathymetryPage from '../Bathymetry/result.jsx';
import ProcessingCSqueezePage from '../CSqueeze/result.jsx';
import ProcessingEUSCoastPage from '../EUSCoast/result.jsx';
import ProcessingMepBayPage from '../MepBay/result.jsx';
import ProcessingIntertidalPage from '../intertidal/result.jsx';
import SelectionPage from '../SelectionPage';
import ProcessingShoreRPage from '../ShoreR/result.jsx';
import ProcessingShoreSPage from '../ShoreS/result.jsx';
import Shoreline from '../Shoreline';
import Bathymetry from '../Bathymetry';
import CSqueeze from '../CSqueeze';
import EUSCoast from '../EUSCoast';
import Intertidal from '../intertidal';
import MepBayPage from '../MepBay/index.jsx';
import ShoreR from '../ShoreR';
import ShoreS from '../ShoreS';
import ActivityIndicator from '../../components/core/ActivityIndicator';
import FAQ from '../FAQ';
import ProblemsPage from '../ProblemsPage';
import CoastlineEvolutionDialog from '../../components/dialog/CoastlineEvolutionDialog';
import MeanTransectDialog from '../../components/dialog/MeanTransectDialog';
import NewLayerDialog from '../../components/dialog/NewLayerDialog';
import ImageSelectionDialog from '../../components/dialog/ImageSelectionDialog';
import AttentionMessage from '../../components/core/AttentionMessage';
import { AppContexts, useThemeMode } from '../../contexts/index.jsx';
import { createAppTheme, followsThemeMode } from '../Shoreline/theme.js';

const ShoreRImageSelectionDialog = (props) => {
  const [t] = useTranslation();
  return (
    <ImageSelectionDialog
      {...props}
      hint={
        <AttentionMessage
          title={t('forms.shoreR.timeSeriesHint.title')}
          message={t('forms.shoreR.timeSeriesHint.message')}
        />
      }
    />
  );
};

const registerDialogs = () => {
  registerDialog('coastlineConfig')(MeanTransectDialog);
  registerDialog('coastlineEvolution')(CoastlineEvolutionDialog);
  registerDialog('newLayer')(NewLayerDialog);
  registerDialog('imageSelection')(ImageSelectionDialog);
  registerDialog('imageSelectionShoreR')(ShoreRImageSelectionDialog);
};
// custom Typography component for showing error message
const NotFound = (props) => (
  <Typography variant="h2" align="center">
    Página não encontrada.
  </Typography>
);

// useSyles hook to inject custom styles
const useStyles = makeStyles((theme) => ({
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
}));

/**
 * @function ThemedPage
 * @description Draws the module selection and the steps in the light/dark mode chosen on the top
 * bar, which used to reach only the result pages. Sits inside `AppContexts`, which holds the mode,
 * and paints the page's own background, since the root `CssBaseline` keeps the body white.
 * @param {Object} props
 * @param {String} props.className
 * @memberof module:Shoreline
 * @returns {JSX.Element}
 */
const ThemedPage = ({ className, children }) => {
  const { shorelineThemeMode } = useThemeMode();
  const outerTheme = useTheme();
  const { pathname } = useLocation();
  const themed = followsThemeMode(pathname);
  const theme = useMemo(
    () => (themed ? createAppTheme(shorelineThemeMode, outerTheme) : outerTheme),
    [themed, shorelineThemeMode, outerTheme],
  );
  return (
    <ThemeProvider theme={theme}>
      <div className={className} style={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
        {children}
      </div>
    </ThemeProvider>
  );
};

// main function
const MainPage = ({ match }) => {
  // Register the dialogs when the component is mounted
  registerDialogs();

  // useStyles hook to inject custom styles
  const classes = useStyles();

  return (
    <AppContexts>
      <ThemedPage className={classes.wrapper}>
        <DialogRoot />
        <NavigationBar />
        <Switch>
          <Route exact strict path={`${match.url}/selection`} component={SelectionPage} />
          <Route exact strict path={`${match.url}/faq`} component={FAQ} />
          <Route exact strict path={`${match.url}/problems`} component={ProblemsPage} />
          <Route exact strict path={`${match.url}/shoreline/:step?`} component={Shoreline} />
          <Route exact strict path={`${match.url}/shoreline/result`} component={ProcessingShorelinePage} />
          <Route exact strict path={`${match.url}/bathymetry/result`} component={ProcessingBathymetryPage} />
          <Route exact strict path={`${match.url}/bathymetry/:step?`} component={Bathymetry} />
          <Route exact strict path={`${match.url}/csqueeze/result`} component={ProcessingCSqueezePage} />
          <Route exact strict path={`${match.url}/csqueeze/:step?`} component={CSqueeze} />
          <Route exact strict path={`${match.url}/euscoast/result`} component={ProcessingEUSCoastPage} />
          <Route exact strict path={`${match.url}/euscoast/:step?`} component={EUSCoast} />
          <Route exact strict path={`${match.url}/intertidal/result`} component={ProcessingIntertidalPage} />
          <Route exact strict path={`${match.url}/intertidal/:step?`} component={Intertidal} />
          <Route exact strict path={`${match.url}/mepbay/result`} component={ProcessingMepBayPage} />
          <Route exact strict path={`${match.url}/mepbay/:step?`} component={MepBayPage} />
          <Route exact strict path={`${match.url}/processing_shore_r`} component={ProcessingShoreRPage} />
          <Route exact strict path={`${match.url}/shore_r/:step?`} component={ShoreR} />
          <Route exact strict path={`${match.url}/processing_shore_s`} component={ProcessingShoreSPage} />
          <Route exact strict path={`${match.url}/shore_s/:step?`} component={ShoreS} />
          <Route
            exact
            strict
            // send the user to the Processing step if the url is processing
            path={`${match.url}/processing`}
            component={ProcessingShorelinePage}
          />
          <Route path={match.url} component={NotFound} />
        </Switch>
        <ActivityIndicator />
      </ThemedPage>
    </AppContexts>
  );
};

export default MainPage;
